import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import React from "react";
import { useTranslationHook } from "../../hooks";

export type StatCardProps = {
  title: string;
  total: number | string;
  active?: number;
  icon?: React.ReactElement;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  flexGrow: 1,
  borderRadius: theme.spacing(2),
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  background: theme.vars
    ? `linear-gradient(180deg, rgba(${theme.vars.palette.background.paperChannel} / 0.6) 0%, rgba(${theme.vars.palette.background.defaultChannel} / 0.8) 100%)`
    : alpha(theme.palette.background.paper, 0.7),
  boxShadow: theme.shadows[2],
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[6],
    transform: "translateY(-2px)",
  },
}));

export default function StatCard({
  title,
  total,
  active,
  icon
}: Readonly<StatCardProps>) {
  const { t } = useTranslationHook();

  const isSingleValue = active === undefined;

  return (
    <StyledCard variant="outlined">
      <CardContent
        sx={{
          px: 4,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
        }}
      >
        {icon && 
          <Box sx={{ mb: 1.5 }}>
            {React.cloneElement(icon, { sx: { fontSize: 40, color: "primary.main" }})}
          </Box>
        }
        
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            textAlign: "center"
          }}
        >
          {title}
        </Typography>

        {isSingleValue ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography 
              variant="h4" 
              component="p"
              sx={{ fontWeight: 600, color: "text.primary", mt: 1 }}
            >
              {total}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("generic.stats.total")}
            </Typography>
          </Box>
        ) : (
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "center",
              px: 4,
              gap: 4
            }}
          >
            <Box>
              <Typography variant="h4" component="p" sx={{ fontWeight: 600 }}>
                {active}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("generic.stats.active")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="h4" component="p" sx={{ fontWeight: 600 }}>
                {total}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("generic.stats.total")}
              </Typography>
            </Box>
          </Stack>
        )}
      </CardContent>
    </StyledCard>
  );
}